import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEventNotes,
  updateEventNotes,
  updatePerformerNotes,
  getAdminEventNotes,
  getMyPerformerNotes,
  updateMyPerformerNotes,
  getMyEventNotes,
} from '../api';
import { performerTierKeys } from './usePerformerTiers';

export const notesKeys = {
  all: ['notes'],
  compare: (eventId) => ['notes', 'compare', eventId],
  adminEvents: ['notes', 'adminEvents'],
  myPerformers: ['notes', 'myPerformers'],
  myEvents: ['notes', 'myEvents'],
};

// Compare opens in its own browser tab (Dashboard uses window.open), so query
// invalidation never crosses tabs. These queries are SQLite-cheap, so they use
// a short staleTime + refetch-on-focus to stay fresh across tabs.
const CROSS_TAB_FRESHNESS = {
  staleTime: 30 * 1000,
  refetchOnWindowFocus: true,
};

// Tier + general notes + individual note for one event (Compare page)
export function useEventNotes(eventId, options = {}) {
  return useQuery({
    queryKey: notesKeys.compare(eventId),
    queryFn: async () => {
      const res = await getEventNotes(eventId);
      return res.data;
    },
    enabled: !!eventId,
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// All individual event notes (Admin Performers tab dropdown)
export function useAdminEventNotes(options = {}) {
  return useQuery({
    queryKey: notesKeys.adminEvents,
    queryFn: async () => {
      const res = await getAdminEventNotes();
      return res.data;
    },
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// General notes for the caller's performers, as a {name: {notes, updatedBy,
// updatedAt}} map (My Artists page; pricer-scoped server-side)
export function useMyPerformerNotes(options = {}) {
  return useQuery({
    queryKey: notesKeys.myPerformers,
    queryFn: async () => {
      const res = await getMyPerformerNotes();
      return res.data.notes;
    },
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// Individual event notes for the caller's performers (My Artists dropdown)
export function useMyEventNotes(options = {}) {
  return useQuery({
    queryKey: notesKeys.myEvents,
    queryFn: async () => {
      const res = await getMyEventNotes();
      return res.data;
    },
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// Save general notes from the My Artists page (pricer-scoped server-side).
// The saved values are merged into the cached map immediately so the page
// never flashes the pre-save text while the invalidation refetch is in flight.
export function useUpdateMyPerformerNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notes) => updateMyPerformerNotes(notes),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(notesKeys.myPerformers, (old) => {
        if (!old) return old;
        const next = { ...old };
        for (const [name, value] of Object.entries(variables)) {
          const trimmed = value.trim();
          const lower = name.toLowerCase();
          const existingKey = Object.keys(next).find(k => k.toLowerCase() === lower) || name;
          if (trimmed) {
            next[existingKey] = { ...(next[existingKey] || {}), notes: trimmed };
          } else {
            delete next[existingKey];
          }
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      queryClient.invalidateQueries({ queryKey: performerTierKeys.all });
    },
  });
}

// Save an event's individual notes (from Compare). Never invalidates
// eventPricing - that would trigger a real Skybox refetch server-side.
export function useUpdateEventNotes(eventId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notes) => updateEventNotes(eventId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
    },
  });
}

// Batch-save general (tour) notes (from Admin Performers tab).
// generalNotes ride on the performer-tiers response, so invalidate that too.
export function useUpdatePerformerNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notes) => updatePerformerNotes(notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performerTierKeys.all });
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
    },
  });
}
